// Phase 2C.3 — client-side company-settings validation.
//
// Mirrors backend/quowada/company.py. The BACKEND IS AUTHORITATIVE: this only
// gives immediate Bulgarian feedback so a user is not round-tripped for an
// obvious mistake. Every rule and message here has a server-side twin.

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$/;
const EIK_RE = /^[0-9]{5,20}$/;
const VAT_RE = /^[A-Z0-9]{5,20}$/;
const PHONE_RE = /^[0-9+()\-/. ]{4,40}$/;
const CURRENCY_RE = /^[A-Z]{3}$/;
// Mirrors organizations_quotation_number_prefix_check (migration 31) and
// quowada/company.py: 1-10 uppercase letters/digits with optional inner dashes.
const PREFIX_RE = /^[A-Z0-9]([A-Z0-9-]{0,8}[A-Z0-9])?$/;

export const MAX_LENGTHS = {
  name: 200,
  legal_name: 300,
  eik: 20,
  vat_number: 20,
  address: 500,
  phone: 40,
  website: 300,
  reply_to_email: 320,
  email_signature: 1000,
  default_currency: 3,
  quotation_number_prefix: 10,
};

export const MESSAGES = {
  name_required: "Името на компанията е задължително.",
  name_too_long: "Името на компанията може да е до 200 знака.",
  legal_name_too_long: "Юридическото наименование може да е до 300 знака.",
  eik_invalid: "ЕИК трябва да съдържа само цифри (от 5 до 20 знака).",
  vat_invalid:
    "ДДС номерът може да съдържа само латински букви и цифри (от 5 до 20 знака).",
  address_too_long: "Адресът може да е до 500 знака.",
  phone_invalid: "Телефонът съдържа непозволени знаци.",
  website_scheme: "Уебсайтът трябва да започва с http:// или https://.",
  website_invalid: "Въведете валиден уебсайт адрес.",
  email_invalid: "Въведете валиден имейл адрес.",
  signature_too_long: "Подписът може да е до 1000 знака.",
  currency_invalid: "Валутата трябва да е код от 3 латински букви, например EUR.",
  prefix_invalid:
    "Префиксът може да съдържа само главни латински букви и цифри (до 10 знака).",
  no_html: "HTML не е разрешен в това поле.",
};

const trim = (v) => (typeof v === "string" ? v.trim() : "");

export function validateField(field, rawValue) {
  const value = field === "email_signature" ? trim(rawValue) : trim(rawValue);
  if (field === "name") {
    if (!value) return MESSAGES.name_required;
    if (value.includes("<") || value.includes(">")) return MESSAGES.no_html;
    if (value.length > MAX_LENGTHS.name) return MESSAGES.name_too_long;
    return null;
  }
  if (field === "quotation_number_prefix") {
    // Never a way to clear the setting: every automatic number needs a prefix.
    const candidate = value.toUpperCase();
    return PREFIX_RE.test(candidate) ? null : MESSAGES.prefix_invalid;
  }
  if (!value) return null;
  switch (field) {
    case "legal_name":
      if (value.includes("<") || value.includes(">")) return MESSAGES.no_html;
      return value.length > MAX_LENGTHS.legal_name
        ? MESSAGES.legal_name_too_long
        : null;
    case "address":
      if (value.includes("<") || value.includes(">")) return MESSAGES.no_html;
      return value.length > MAX_LENGTHS.address ? MESSAGES.address_too_long : null;
    case "eik":
      return EIK_RE.test(value) ? null : MESSAGES.eik_invalid;
    case "vat_number":
      return VAT_RE.test(value.replace(/ /g, "").toUpperCase())
        ? null
        : MESSAGES.vat_invalid;
    case "phone":
      return PHONE_RE.test(value) ? null : MESSAGES.phone_invalid;
    case "website": {
      const lowered = value.toLowerCase();
      if (!lowered.startsWith("http://") && !lowered.startsWith("https://")) {
        return MESSAGES.website_scheme;
      }
      if (value.length > MAX_LENGTHS.website) return MESSAGES.website_invalid;
      const host = value.split("://")[1].split("/")[0].split("?")[0];
      if (!host || host.includes(" ") || host.includes("@") || !host.replace(/^\.+|\.+$/g, "").includes(".")) {
        return MESSAGES.website_invalid;
      }
      return null;
    }
    case "reply_to_email":
      if (value.includes(",") || value.includes(";")) return MESSAGES.email_invalid;
      return EMAIL_RE.test(value) ? null : MESSAGES.email_invalid;
    case "email_signature":
      return value.length > MAX_LENGTHS.email_signature
        ? MESSAGES.signature_too_long
        : null;
    case "default_currency":
      return CURRENCY_RE.test(value.toUpperCase())
        ? null
        : MESSAGES.currency_invalid;
    default:
      return null;
  }
}

/** Validate a whole draft; returns `{field: message}` (empty when valid). */
export function validateSettings(draft, fields) {
  const errors = {};
  fields.forEach((field) => {
    const message = validateField(field, draft[field]);
    if (message) errors[field] = message;
  });
  return errors;
}

/** Field-level errors returned by the API, if any. */
export function apiFieldErrors(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.errors) return detail.errors;
  return null;
}

// Bulgarian text for the backend's stable permission/lookup slugs. Anything
// unknown falls back to the generic message — a raw slug is never shown.
const API_ERROR_MESSAGES = {
  owner_only: "Само собственикът на организацията може да променя тези настройки.",
  member_inactive: "Достъпът ви до тази организация е деактивиран.",
  not_a_member: "Нямате достъп до тази организация.",
  forbidden_or_not_found: "Нямате достъп до тази организация.",
  nothing_to_update: "Няма промени за запазване.",
  not_found: "Организацията не беше намерена.",
};

export function apiErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && API_ERROR_MESSAGES[detail]) {
    return API_ERROR_MESSAGES[detail];
  }
  return fallback;
}
